import {
  SimulatorResult,
  SimulatorResultItem,
  SimulatorResultBaseItem,
  AttachmentResult,
  AttachmentType,
  ConfirmedQualificationResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import { ProcessingState, ServiceReportExtended } from '@sdir/httpclient/lib/clients/aps/seagoing';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { routes } from '../constants/routes';
import { AddedQualifications, QualificationType } from '../types';
import { ToggleDisableNewSeagoing } from '../constants/permissions';

export const isExternalUser = () => {
  return window.sessionStorage.getItem('user_type') === 'external';
};

export const findQualification = (
  appQualification: SimulatorResult,
  uri: string
): SimulatorResultItem | null => {
  const baseMatch = appQualification.base?.items?.find((x) => x.uri === uri);
  if (baseMatch) return baseMatch;
  if (appQualification.alternatives) {
    for (let i = 0; i < appQualification.alternatives.length; i += 1) {
      const altMatch = appQualification.alternatives[i].items?.find((x) => x.uri === uri);
      if (altMatch) return altMatch;
    }
  }
  return null;
};

const checkSeagoingAttachmentGroupIsValid = (
  groupUid: string,
  addedSeagoing: ServiceReportExtended[],
  includeRejected?: boolean
) => {
  const matchingSeagoing = addedSeagoing.filter((x) => x.applicationGroupUid === groupUid);
  if (includeRejected) return !!matchingSeagoing.length;
  return (
    !!matchingSeagoing.length &&
    !matchingSeagoing.find(
      (x) => x.qualificationApprovalState?.processingState === ProcessingState.Rejected
    )
  );
};

const checkSeagoingAttachmentGroupIsApproved = (
  groupUid: string,
  addedSeagoing: ServiceReportExtended[]
) => {
  const matchingSeagoing = addedSeagoing.filter((x) => x.applicationGroupUid === groupUid);
  // All added seagoing in the attachment group should be approved
  const approved =
    matchingSeagoing?.filter(
      (y) => y.qualificationApprovalState?.processingState === ProcessingState.Approved
    ) || [];
  return !!matchingSeagoing.length && matchingSeagoing.length === approved.length;
};

const checkSeagoingAttachmentGroupIsRejected = (
  groupUid: string,
  addedSeagoing: ServiceReportExtended[]
) => {
  const matchingSeagoing = addedSeagoing.filter((x) => x.applicationGroupUid === groupUid);
  return (
    !!matchingSeagoing.length &&
    !!matchingSeagoing.find(
      (x) => x.qualificationApprovalState?.processingState === ProcessingState.Rejected
    )
  );
};

export const attachmentIsValid = (
  attachment: AttachmentResult,
  addedQualifications: AddedQualifications,
  includeRejected?: boolean
): boolean => {
  const isExternal = isExternalUser();
  if (isExternal) return !!attachment.group;
  switch (attachment.type) {
    case AttachmentType.SeagoingTime:
      return (
        !!attachment.group &&
        checkSeagoingAttachmentGroupIsValid(
          attachment.group,
          addedQualifications.seagoing,
          includeRejected
        )
      );
    case AttachmentType.AdditionalCompetence:
    case AttachmentType.Assessment:
    case AttachmentType.ForeignCourse:
    case AttachmentType.Course:
    case AttachmentType.Education:
      return !!attachment.group;
    default:
      return true;
  }
};

export const attachmentIsApproved = (
  attachment: AttachmentResult,
  addedQualifications: AddedQualifications
): boolean => {
  if (attachment.isApproved) return true;
  switch (attachment.type) {
    case AttachmentType.SeagoingTime:
      return (
        !!attachment.group &&
        checkSeagoingAttachmentGroupIsApproved(attachment.group, addedQualifications.seagoing)
      );
    case AttachmentType.AdditionalCompetence:
    case AttachmentType.Course:
    case AttachmentType.Education:
    case AttachmentType.HealthCheck:
    case AttachmentType.Photo:
    case AttachmentType.PhotoCropped:
    case AttachmentType.Signature:
    case AttachmentType.SignatureCropped:
    case AttachmentType.Certificate:
    case AttachmentType.ForeignCourse:
    case AttachmentType.Confirmation:
    case AttachmentType.Assessment:
    case AttachmentType.ForeignId:
    case AttachmentType.NorwegianPassportOrId:
      return !!attachment.isApproved;
    default:
      return true;
  }
};

export const attachmentIsRejected = (
  attachment: AttachmentResult,
  addedQualifications: AddedQualifications
): boolean => {
  switch (attachment.type) {
    case AttachmentType.SeagoingTime:
      return (
        !!attachment.group &&
        checkSeagoingAttachmentGroupIsRejected(attachment.group, addedQualifications.seagoing)
      );
    case AttachmentType.AdditionalCompetence:
    case AttachmentType.Course:
    case AttachmentType.Education:
    case AttachmentType.HealthCheck:
    case AttachmentType.Photo:
    case AttachmentType.PhotoCropped:
    case AttachmentType.Signature:
    case AttachmentType.SignatureCropped:
    case AttachmentType.Certificate:
    case AttachmentType.ForeignCourse:
    case AttachmentType.Assessment:
    case AttachmentType.ForeignId:
    case AttachmentType.NorwegianPassportOrId:
    case AttachmentType.Confirmation:
      return attachment.isApproved === false;
    default:
      return false;
  }
};

export const attachmentMatchesQualification = (
  qualification: SimulatorResultItem,
  attachment: AttachmentResult
) => {
  const uris = attachment.simulatorResultItemUri?.split(',') || [];
  return qualification.uri && uris.includes(qualification.uri);
  // return qualification.uri === attachment.simulatorResultItemUri;
};

export const findQualificationAttachment = (
  qualification: SimulatorResultItem,
  attachments: AttachmentResult[]
) => {
  const filtered = attachments.filter((a) => attachmentMatchesQualification(qualification, a));
  if (!filtered || filtered.length === 0) return undefined;
  const newestMatchingAttachment = filtered.reduce((newest, item) =>
    (newest.sortIndex || 0) > (item.sortIndex || 0) ? newest : item
  );
  return newestMatchingAttachment;
};

const hasAnyAddedInfoNewSeagoing = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  types: string[]
) => {
  const alternatives = addedQualifications?.newSeagoing?.alternatives;
  let hasAnyInfo = false;
  alternatives?.forEach((a) => {
    const detail = a?.alternativeDetails?.find((d) => d.sgsCode === qualification?.uri);
    if (detail) {
      let items = [];
      types.forEach((type) => {
        items = items.concat(a?.[type]);
      });
      if (items.length > 0) {
        hasAnyInfo = true;
      }
    }
  });
  return hasAnyInfo;
};

export const enabledNewSeagoing = () => {
  const toggles = window?.sessionStorage?.getItem('toggles') as string;
  if (!toggles) return false;
  const togglesObject = JSON.parse(toggles);
  return !isToggleOn(togglesObject, ToggleDisableNewSeagoing, null);
};

const hasValidAddedInfoNewSeagoing = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications
) => {
  const alternatives = addedQualifications?.newSeagoing?.alternatives;
  let hasValidInfo = false;
  alternatives?.forEach((a) => {
    const detail = a?.alternativeDetails?.find((d) => d.sgsCode === qualification?.uri);
    if (detail) {
      if (detail.qualifiedDaysMissing === 0) {
        hasValidInfo = true;
      }
    }
  });
  return hasValidInfo;
};

export const qualificationHasValidAddedInfo = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[]
) => {
  const isExternal = isExternalUser();
  const newSeagoing = enabledNewSeagoing();
  if (qualification?.type === QualificationType.SeagoingTime && newSeagoing && !isExternal) {
    return hasValidAddedInfoNewSeagoing(qualification, addedQualifications);
  }
  const matchingAttachment = findQualificationAttachment(qualification, attachments);
  if (matchingAttachment) {
    return attachmentIsValid(matchingAttachment, addedQualifications, false);
  }
  return false;
};

export const qualificationHasAnyAddedInfo = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[]
) => {
  const newSeagoing = enabledNewSeagoing();
  if (qualification?.type === QualificationType.SeagoingTime && newSeagoing) {
    return hasAnyAddedInfoNewSeagoing(qualification, addedQualifications, [
      'applicationServiceReportsUsed',
      'unusedApplicationServiceReports',
    ]);
  }
  const matchingAttachment = findQualificationAttachment(qualification, attachments);
  if (matchingAttachment) {
    return attachmentIsValid(matchingAttachment, addedQualifications, true);
  }
  return false;
};

export const qualificationHasApprovedAddedInfo = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[]
) => {
  const matchingAttachment = findQualificationAttachment(qualification, attachments);

  if (matchingAttachment?.type === AttachmentType.Photo) {
    const crop = attachments?.reverse?.()?.find((a) => a.type === AttachmentType.PhotoCropped);
    if (crop) {
      return attachmentIsApproved(crop, addedQualifications);
    }
  } else if (matchingAttachment?.type === AttachmentType.Signature) {
    const crop = attachments?.reverse?.()?.find((a) => a.type === AttachmentType.SignatureCropped);
    if (crop) {
      return attachmentIsApproved(crop, addedQualifications);
    }
  }

  if (matchingAttachment) {
    return attachmentIsApproved(matchingAttachment, addedQualifications);
  }
  return false;
};

export const qualificationHasRejectedAddedInfo = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[]
) => {
  const matchingAttachment = findQualificationAttachment(qualification, attachments);

  if (matchingAttachment?.type === AttachmentType.Photo) {
    const crop = attachments?.reverse?.()?.find((a) => a.type === AttachmentType.PhotoCropped);
    if (crop) {
      return attachmentIsRejected(crop, addedQualifications);
    }
  } else if (matchingAttachment?.type === AttachmentType.Signature) {
    const crop = attachments?.reverse?.()?.find((a) => a.type === AttachmentType.SignatureCropped);
    if (crop) {
      return attachmentIsRejected(crop, addedQualifications);
    }
  }

  if (matchingAttachment) {
    return attachmentIsRejected(matchingAttachment, addedQualifications);
  }
  return false;
};

export const qualificationExists = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[]
) =>
  qualification.status ||
  qualificationHasAnyAddedInfo(qualification, addedQualifications, attachments);

export const qualificationIsComplete = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[],
  confirmations?: ConfirmedQualificationResult[]
) => {
  if (qualification.type === QualificationType.Declaration) {
    return !!confirmations?.find(
      (q) => q.qualificationUri?.toLowerCase() === qualification.uri?.toLowerCase()
    );
  }
  return (
    qualification.status ||
    qualificationHasValidAddedInfo(qualification, addedQualifications, attachments)
  );
};

export const qualificationIsApproved = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[],
  confirmations?: ConfirmedQualificationResult[]
) => {
  if (qualification.type === QualificationType.Declaration) {
    return !!confirmations?.find(
      (q) => q.qualificationUri?.toLowerCase() === qualification.uri?.toLowerCase()
    );
  }
  return (
    qualification.status ||
    qualificationHasApprovedAddedInfo(qualification, addedQualifications, attachments)
  );
};

export const qualificationIsRejected = (
  qualification: SimulatorResultItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[]
) =>
  !qualification.status &&
  qualificationHasRejectedAddedInfo(qualification, addedQualifications, attachments);

export const qualificationSetIsComplete = (
  qualifications: SimulatorResultBaseItem,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[],
  confirmations?: ConfirmedQualificationResult[]
) => {
  const hasDeclaration = qualifications?.items?.some(
    (q) => q.type === QualificationType.Declaration
  );
  if (qualifications.status && !hasDeclaration) return true;
  const incompleteQualification = qualifications.items?.find(
    (q) => !qualificationIsComplete(q, addedQualifications, attachments, confirmations)
  );
  return !incompleteQualification;
};

export const hasValidAlternative = (
  appQualification: SimulatorResult,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[],
  confirmations?: ConfirmedQualificationResult[]
) => {
  const validAlternative = appQualification.alternatives?.find((a) =>
    qualificationSetIsComplete(a, addedQualifications, attachments, confirmations)
  );
  return !!validAlternative;
};

export const hasValidQualifications = (
  appQualification: SimulatorResult,
  addedQualifications: AddedQualifications,
  attachments: AttachmentResult[],
  confirmations?: ConfirmedQualificationResult[]
): boolean => {
  if (appQualification.status) return true;
  const baseQualificationsOk =
    appQualification.base &&
    qualificationSetIsComplete(
      appQualification.base,
      addedQualifications,
      attachments,
      confirmations
    );
  if (!baseQualificationsOk) return false;
  return (
    !appQualification.alternatives?.length ||
    hasValidAlternative(appQualification, addedQualifications, attachments, confirmations)
  );
};

export const errorTranslationForQualification = (qualification: SimulatorResultItem) => {
  switch (qualification.type) {
    case QualificationType.Education:
    case QualificationType.Course:
      return 'qualification.errors.uploaddoctocontinue';
    default:
      return 'qualification.errors.requirementnotmet';
  }
};

export const editRouteForAttachmentType = (attachmentType: AttachmentType): string => {
  switch (attachmentType) {
    case AttachmentType.SeagoingTime:
      return routes.application.editSeagoing;
    case AttachmentType.Education:
    case AttachmentType.Course:
      return routes.application.editCourse;
    case AttachmentType.Assessment:
      return routes.application.addAssessment;
    case AttachmentType.AdditionalCompetence:
      return routes.application.addAdditionalCompetence;
    default:
      return routes.application.addFiles;
  }
};

export const QUALIFICATION_URI = 'qualification_uri';
export const QUALIFICATION_ATTACHMENTTYPE = 'qualification_attachmenttype';

export const getFromStorage = (attr: string) => {
  return window?.sessionStorage?.getItem(attr) || '';
};

export const setInStorage = (attr, value) => {
  return window?.sessionStorage?.setItem(attr, value);
};
